'use client';

import type {
  CompetitorBenchmarkDto,
  CompetitorNearbyPlaceDto,
  GooglePlaceDto,
} from '@up2stay/up2promo-api-client';
import {
  Card,
  ResponsiveDialog,
  SegmentedControl,
  Switch,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  Skeleton,
  cn,
} from '@up2stay/up2promo-ui';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { MapPin, Radar } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { CompetitorMap } from '@/core/components/reputation/competitor-map';
import { CompetitorSampleReviews } from '@/core/components/reputation/competitor-sample-reviews';
import {
  formatPinRating,
  pinToneVsYou,
} from '@/core/components/reputation/competitor-map-pins';
import {
  competitorMapCoverKm,
  formatCompetitorRadiusHint,
} from '@/core/components/reputation/competitor-radius';
import { CompetitorBriefingCard } from '@/core/components/reputation/competitors/competitor-briefing-card';
import { competitorDisplayNames } from '@/core/components/reputation/competitors/competitor-display-names';
import { CompetitorStandingCard } from '@/core/components/reputation/competitors/competitor-standing-card';
import { computeStanding } from '@/core/components/reputation/competitors/competitor-standing';
import {
  CompetitorSuggestionsCard,
  pickSuggestions,
} from '@/core/components/reputation/competitors/competitor-suggestions-card';
import { CompetitorTrackedCard } from '@/core/components/reputation/competitors/competitor-tracked-card';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import {
  useAddCompetitor,
  useCompetitorBoardSummary,
  useCompetitorNearby,
  useListCompetitors,
  useRemoveCompetitor,
} from '@/core/hooks/use-competitor-benchmark';
import {
  COMPETITOR_RADIUS_STEPS,
  useCompetitorMapPrefs,
} from '@/core/hooks/use-competitor-map-prefs';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { useDictionary, useLocale } from '@/core/providers/dictionary-provider';
import { EmptyStage } from '@/core/components/reputation/competitor-theater-empty-stage';
import { PlaceActions } from '@/core/components/reputation/competitor-theater-place-actions';
import { PlaceDetailBody } from '@/core/components/reputation/competitor-theater-place-detail';
import { PlaceStats } from '@/core/components/reputation/competitor-theater-place-stats';
import { type CompetitorTheaterLabels } from '@/core/components/reputation/competitor-theater-shared';

export type { CompetitorTheaterLabels };

const MAX_COMPETITORS = 5;
/** Ranked shortlist under the map; full discovery stays on pins. */
const DISCOVERY_LIST_LIMIT = 8;

const springPanel = {
  type: 'spring' as const,
  stiffness: 380,
  damping: 32,
  mass: 0.85,
};

function apiErrorCode(error: unknown): string | undefined {
  return (error as { response?: { data?: { error?: string } } })?.response?.data
    ?.error;
}

export function CompetitorTheater({
  workspaceId,
  locationId,
  locationName,
  yourAverageRating,
  yourTotalReviews,
  labels,
}: {
  workspaceId: string;
  locationId: string;
  locationName: string;
  yourAverageRating?: number;
  yourTotalReviews?: number;
  labels: CompetitorTheaterLabels;
}) {
  const competitorsDict = useDictionary().reputation.competitorsPage;
  const isMobile = useIsMobile();
  const reduceMotion = useReducedMotion();
  const locale = useLocale();
  const { data, isLoading } = useListCompetitors(workspaceId, locationId);
  const boardSummary = useCompetitorBoardSummary(workspaceId, locationId);
  const addMutation = useAddCompetitor(workspaceId, locationId);
  const removeMutation = useRemoveCompetitor(workspaceId, locationId);

  const competitors = data?.competitors;
  const competitorList = useMemo(() => competitors ?? [], [competitors]);
  const displayNames = useMemo(
    () => competitorDisplayNames(competitorList),
    [competitorList],
  );
  const atLimit = competitorList.length >= MAX_COMPETITORS;
  const prefs = useCompetitorMapPrefs(locationId, competitorList.length);
  const nearbyQuery = useCompetitorNearby(
    workspaceId,
    locationId,
    prefs.radiusMeters,
  );
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);

  const nearbyErrorCode = apiErrorCode(nearbyQuery.error);
  const missingCoordinates = nearbyErrorCode === 'locationCoordinatesRequired';
  const nearbyUnavailable =
    nearbyErrorCode === 'googleMapsApiKeyNotConfigured' ||
    (!!nearbyQuery.error && !missingCoordinates);

  const nearbyPlaces = nearbyQuery.data?.places;
  const mapPlaces = useMemo(() => {
    const byId = new Map<string, CompetitorNearbyPlaceDto>();
    for (const place of nearbyPlaces ?? []) {
      byId.set(place.placeId, place);
    }
    for (const competitor of competitors ?? []) {
      if (
        competitor.latitude == null ||
        competitor.longitude == null ||
        !Number.isFinite(competitor.latitude) ||
        !Number.isFinite(competitor.longitude)
      ) {
        continue;
      }
      const existing = byId.get(competitor.competitorPlaceId);
      byId.set(competitor.competitorPlaceId, {
        placeId: competitor.competitorPlaceId,
        name: competitor.name,
        address: competitor.address,
        rating: competitor.rating ?? existing?.rating,
        reviewCount: competitor.reviewCount ?? existing?.reviewCount,
        latitude: competitor.latitude,
        longitude: competitor.longitude,
        types: existing?.types ?? [],
        alreadyTracked: true,
        competitorId: competitor.id,
      });
    }
    return [...byId.values()];
  }, [competitors, nearbyPlaces]);

  // Switch off: the map shows only you and your tracked competitors.
  const visiblePlaces = useMemo(
    () =>
      prefs.showNearbyOnMap
        ? mapPlaces
        : mapPlaces.filter((place) => place.alreadyTracked),
    [mapPlaces, prefs.showNearbyOnMap],
  );

  const selectedPlace = useMemo(
    () =>
      visiblePlaces.find((place) => place.placeId === selectedPlaceId) ?? null,
    [visiblePlaces, selectedPlaceId],
  );
  const selectedSampleReviews = useMemo(
    () =>
      competitorList.find((c) => c.id === selectedPlace?.competitorId)
        ?.sampledReviews ?? [],
    [competitorList, selectedPlace?.competitorId],
  );

  const [prevLocationId, setPrevLocationId] = useState(locationId);
  if (locationId !== prevLocationId) {
    setPrevLocationId(locationId);
    setSelectedPlaceId(null);
  }

  const handleAddNearby = (place: CompetitorNearbyPlaceDto) => {
    if (atLimit) {
      toast.error(labels.limitReached);
      return;
    }
    if (place.alreadyTracked) return;
    addMutation.mutate(
      {
        competitorPlaceId: place.placeId,
        name: place.name,
        address: place.address,
        latitude: place.latitude,
        longitude: place.longitude,
      },
      {
        onError: (error) => {
          const code = apiErrorCode(error);
          if (code === 'competitorLimitReached') {
            toast.error(labels.limitReached);
            return;
          }
          toast.error(labels.addError);
        },
        onSuccess: () => setSelectedPlaceId(place.placeId),
      },
    );
  };

  const handleAddSearch = (place: GooglePlaceDto) => {
    if (atLimit) {
      toast.error(labels.limitReached);
      return;
    }
    addMutation.mutate(
      {
        competitorPlaceId: place.id,
        name: place.name,
        address: place.address,
        latitude: place.latitude,
        longitude: place.longitude,
      },
      {
        onError: (error) => {
          const code = apiErrorCode(error);
          if (code === 'competitorLimitReached') {
            toast.error(labels.limitReached);
            return;
          }
          toast.error(labels.addError);
        },
      },
    );
  };

  // Removing is instant; the toast offers an undo that tracks the same
  // place again (DEV-822).
  const handleRemove = (competitorId: string) => {
    const removed = competitorList.find((c) => c.id === competitorId);
    removeMutation.mutate(competitorId, {
      onSuccess: () => {
        if (selectedPlace?.competitorId === competitorId) {
          setSelectedPlaceId(null);
        }
        if (!removed) return;
        toast.success(
          labels.removed.replace(
            '{name}',
            displayNames.get(removed.id) ?? removed.name,
          ),
          {
            action: {
              label: labels.undo,
              onClick: () =>
                addMutation.mutate(
                  {
                    competitorPlaceId: removed.competitorPlaceId,
                    name: removed.name,
                    address: removed.address,
                    latitude: removed.latitude,
                    longitude: removed.longitude,
                  },
                  { onError: () => toast.error(labels.addError) },
                ),
            },
          },
        );
      },
    });
  };

  const radiusHintText = nearbyQuery.data
    ? formatCompetitorRadiusHint({
        coverKm: competitorMapCoverKm({
          own: nearbyQuery.data.own,
          places: mapPlaces,
          nearbyRadiusMeters: nearbyQuery.data.radiusMeters,
        }),
        nearbyRadiusMeters: nearbyQuery.data.radiusMeters,
        radiusHint: labels.radiusHint,
        radiusHintTracked: labels.radiusHintTracked,
      })
    : '';

  const standing = useMemo(
    () =>
      computeStanding(
        {
          name: locationName,
          rating: yourAverageRating,
          reviewCount: yourTotalReviews,
        },
        competitorList.map((competitor) => ({
          id: competitor.id,
          name: displayNames.get(competitor.id) ?? competitor.name,
          rating: competitor.rating,
          reviewCount: competitor.reviewCount,
        })),
      ),
    [
      competitorList,
      displayNames,
      locationName,
      yourAverageRating,
      yourTotalReviews,
    ],
  );

  // Proposals are independent of the map switch: always offered below 5.
  const suggestions = useMemo(
    () => (atLimit ? [] : pickSuggestions(mapPlaces)),
    [mapPlaces, atLimit],
  );
  const nearbyOk = !!nearbyQuery.data && !nearbyQuery.data.reason;

  const discoveryNotice =
    nearbyQuery.data?.reason === 'unsupported_preset'
      ? labels.unsupportedPreset
      : undefined;

  const mapCard = missingCoordinates ? (
    <EmptyStage
      icon={<MapPin className="size-9" aria-hidden />}
      message={labels.noCoordinates}
    />
  ) : nearbyQuery.isLoading ? (
    <Skeleton className="h-[360px] w-full rounded-[var(--r-card)] sm:h-[480px]" />
  ) : nearbyUnavailable ? (
    <EmptyStage message={labels.nearbyError} />
  ) : nearbyQuery.data ? (
    <Card className="relative isolate flex min-w-0 flex-col overflow-hidden">
      <div className="relative">
        <CompetitorMap
          theater
          className="rounded-none border-0"
          own={{ ...nearbyQuery.data.own, rating: yourAverageRating }}
          places={visiblePlaces}
          selectedPlaceId={selectedPlaceId}
          onSelectPlace={(place) => setSelectedPlaceId(place?.placeId ?? null)}
          yourAverageRating={yourAverageRating}
          nearbyRadiusMeters={nearbyQuery.data.radiusMeters}
          labels={{
            mapUnavailable: labels.mapUnavailable,
            yourLocation: labels.yourLocation,
            legendTracked: labels.legendTracked,
            legendSuggested: labels.legendSuggested,
            youShort: labels.youShort,
            legendThreat: labels.legendThreat,
            legendAhead: labels.legendAhead,
            legendEven: labels.legendEven,
            clusterCount: labels.clusterCount,
            recenterMap: labels.recenterMap,
          }}
        />

        {!isMobile ? (
          <AnimatePresence>
            {selectedPlace ? (
              <motion.aside
                key={selectedPlace.placeId}
                initial={reduceMotion ? false : { opacity: 0, x: 28 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 24 }}
                transition={springPanel}
                className="absolute inset-y-0 right-0 z-20 flex w-[min(340px,60%)] flex-col overflow-y-auto border-l border-border bg-card/95 p-5 shadow-[-12px_0_32px_-16px_rgb(0_0_0/0.2)] backdrop-blur-sm"
                data-map-optical-overlay
              >
                <PlaceDetailBody
                  place={selectedPlace}
                  sampleReviews={selectedSampleReviews}
                  labels={labels}
                  yourAverageRating={yourAverageRating}
                  atLimit={atLimit}
                  isAdding={addMutation.isPending}
                  isRemoving={removeMutation.isPending}
                  onTrack={() => handleAddNearby(selectedPlace)}
                  onRemove={() => {
                    if (selectedPlace.competitorId) {
                      handleRemove(selectedPlace.competitorId);
                    }
                  }}
                  onClose={() => setSelectedPlaceId(null)}
                />
              </motion.aside>
            ) : null}
          </AnimatePresence>
        ) : null}
      </div>

      {/* Wraps so the shrink-0 controls never squeeze the hint to 0px when
          the map card is narrow (two-column xl layout, phones) — DEV-433. */}
      <div className="text-muted-foreground flex flex-wrap items-center gap-x-2.5 gap-y-2 border-t bg-[color:var(--surface-1)] px-4 py-2.5 text-[12.5px]">
        <Radar className="size-[15px] shrink-0" aria-hidden />
        {/* Wraps instead of cutting off the distance; the 14rem basis moves
            the controls to the next line before the text gets squeezed
            (DEV-822). */}
        <span className="min-w-0 flex-[1_1_14rem]">
          {visiblePlaces.length === 0 ? labels.nearbyEmpty : radiusHintText}
        </span>
        {nearbyOk ? (
          <label className="ml-auto flex shrink-0 items-center gap-2 text-xs">
            <span className="max-sm:hidden">
              {competitorsDict.radius.showOnMap}
            </span>
            <span className="sm:hidden">
              {competitorsDict.radius.showOnMapShort}
            </span>
            <Switch
              checked={prefs.showNearbyOnMap}
              onCheckedChange={prefs.setShowNearbyOnMap}
              aria-label={competitorsDict.radius.showOnMap}
            />
          </label>
        ) : null}
        {nearbyOk ? (
          <SegmentedControl
            ariaLabel={competitorsDict.radius.label}
            value={String(prefs.radiusMeters)}
            onChange={(next) =>
              prefs.setRadiusMeters(
                Number(next) as (typeof COMPETITOR_RADIUS_STEPS)[number],
              )
            }
            options={COMPETITOR_RADIUS_STEPS.map((meters) => ({
              value: String(meters),
              label: `${meters / 1000} km`,
            }))}
            itemClassName="h-7 px-2.5 text-xs"
          />
        ) : null}
        {labels.limitation ? (
          <SettingsInfoHint label={labels.limitationLabel}>
            {labels.limitation}
          </SettingsInfoHint>
        ) : null}
      </div>
    </Card>
  ) : null;

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        {mapCard}
        {competitorList.length === 0 ? (
          <CompetitorSuggestionsCard
            suggestions={suggestions}
            isAdding={addMutation.isPending}
            dict={competitorsDict.suggestions}
            locale={locale}
            onTrack={handleAddNearby}
            onFocus={(place) => setSelectedPlaceId(place.placeId)}
          />
        ) : (
          <CompetitorStandingCard
            standing={standing}
            yourRating={yourAverageRating}
            dict={competitorsDict.standing}
            locale={locale}
          />
        )}
      </div>

      {competitorList.length > 0 ? (
        <CompetitorBriefingCard
          summary={boardSummary.data}
          loading={
            boardSummary.isLoading || boardSummary.data?.status === 'generating'
          }
          dict={competitorsDict.briefing}
          locale={locale}
        />
      ) : null}

      <CompetitorTrackedCard
        competitors={competitorList}
        isLoading={isLoading}
        atLimit={atLimit}
        isAdding={addMutation.isPending}
        isRemoving={removeMutation.isPending}
        dict={competitorsDict.tracked}
        notice={discoveryNotice}
        locale={locale}
        suggestions={suggestions}
        suggestionsEnabled={nearbyOk}
        suggestionsDict={competitorsDict.suggestions}
        onTrackSuggestion={handleAddNearby}
        onFocusSuggestion={(place) => setSelectedPlaceId(place.placeId)}
        onAdd={handleAddSearch}
        onRemove={handleRemove}
        onSelect={(competitor) => {
          if (competitor.latitude != null && competitor.longitude != null) {
            setSelectedPlaceId(competitor.competitorPlaceId);
          }
        }}
      />

      {isMobile ? (
        <ResponsiveDialog
          open={selectedPlace != null}
          onOpenChange={(open) => {
            if (!open) setSelectedPlaceId(null);
          }}
        >
          <ResponsiveDialogContent className="flex max-h-[88vh] flex-col gap-0 p-0 sm:max-w-md">
            {selectedPlace ? (
              <>
                <ResponsiveDialogHeader className="space-y-1 px-6 pt-6">
                  <ResponsiveDialogTitle className="text-wrap break-words">
                    {selectedPlace.name}
                  </ResponsiveDialogTitle>
                  {selectedPlace.address ? (
                    <ResponsiveDialogDescription>
                      {selectedPlace.address}
                    </ResponsiveDialogDescription>
                  ) : null}
                </ResponsiveDialogHeader>
                <div className="space-y-4 overflow-y-auto px-6 py-3">
                  <PlaceStats
                    place={selectedPlace}
                    labels={labels}
                    yourAverageRating={yourAverageRating}
                  />
                  <CompetitorSampleReviews
                    reviews={selectedSampleReviews}
                    title={labels.sampleReviews}
                  />
                </div>
                <ResponsiveDialogFooter className="flex-col gap-2 border-t px-6 py-4 sm:flex-col">
                  <PlaceActions
                    place={selectedPlace}
                    labels={labels}
                    atLimit={atLimit}
                    isAdding={addMutation.isPending}
                    isRemoving={removeMutation.isPending}
                    onTrack={() => handleAddNearby(selectedPlace)}
                    onRemove={() => {
                      if (selectedPlace.competitorId) {
                        handleRemove(selectedPlace.competitorId);
                      }
                    }}
                  />
                </ResponsiveDialogFooter>
              </>
            ) : null}
          </ResponsiveDialogContent>
        </ResponsiveDialog>
      ) : null}
    </div>
  );
}
